import { useState, useEffect } from "react";
import { CityStats, calculateCityStats, calculateNationalStats, getTopCitiesByDealerCount } from "@/services/seo/cityStatsService";
import { isSSR } from "@/lib/isSSR";

interface UseSEOStatsOptions {
  city?: string;
  enabled?: boolean;
  // Pre-loaded data from SSR
  initialStats?: CityStats | null;
  initialTopCities?: Array<{city: string, count: number}> | null;
}

interface UseSEOStatsReturn {
  stats: CityStats | null;
  topCities: Array<{city: string, count: number}> | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch SEO statistics for dealers
 * Can be used for both national and city-specific stats
 * Supports pre-loaded data from SSR
 */
export function useSEOStats({ 
  city, 
  enabled = true, 
  initialStats = null, 
  initialTopCities = null 
}: UseSEOStatsOptions = {}): UseSEOStatsReturn {
  const [stats, setStats] = useState<CityStats | null>(initialStats);
  const [topCities, setTopCities] = useState<Array<{city: string, count: number}> | null>(initialTopCities);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If we have initial data from SSR, don't fetch again
    if (initialStats || initialTopCities) {
      console.log(`[SEO Stats] Using pre-loaded SSR data for: ${city || 'national'}`);
      return;
    }

    // Don't run on server side or if disabled
    if (isSSR() || !enabled) {
      return;
    }

    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log(`[SEO Stats] Fetching stats for: ${city || 'national'}`);

        if (city) {
          // Fetch city-specific stats
          const cityStats = await calculateCityStats(city);
          setStats(cityStats);
          setTopCities(null); // Not needed for city pages
        } else {
          // Fetch national stats and top cities
          const [nationalStats, cities] = await Promise.all([
            calculateNationalStats(),
            getTopCitiesByDealerCount(10)
          ]);
          setStats(nationalStats);
          setTopCities(cities);
        }

        console.log(`[SEO Stats] Successfully fetched stats for: ${city || 'national'}`);
      } catch (err) {
        console.error(`[SEO Stats] Error fetching stats for ${city || 'national'}:`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stats');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [city, enabled, initialStats, initialTopCities]);

  return {
    stats,
    topCities,
    isLoading,
    error
  };
}
